// src/pages/LoginPage.jsx
// ORBIS PMO — C2.7.2B Login visual + resolver de endpoint de autenticación
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../config/orbisConfig";

// Ajusta aquí si tu endpoint real tiene otro nombre.
// El login probará estos endpoints en orden y saltará los que devuelvan HTML.
const LOGIN_ENDPOINTS = [
    `${API_BASE}/auth_login.php`,
];


function normalizeAssetPath(path) {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/orbispmo/")) return raw;
    if (raw.startsWith("/PM/")) return `/orbispmo/${raw.slice(4).replace(/^\/+/, "")}`;
    if (raw.startsWith("/")) return raw;
    return `/orbispmo/${raw.replace(/^\/+/, "")}`;
}

function normalizeSettingsPayload(payload = {}) {
    const out = {};
    if (Array.isArray(payload)) {
        payload.forEach((row) => {
            const key = row?.setting_key || row?.key;
            if (key) out[key] = normalizeAssetPathIfNeeded(key, row?.setting_value ?? row?.value ?? "");
        });
        return out;
    }

    Object.entries(payload || {}).forEach(([key, row]) => {
        if (row && typeof row === "object" && !Array.isArray(row)) {
            out[key] = normalizeAssetPathIfNeeded(key, row?.setting_value ?? row?.value ?? "");
        } else {
            out[key] = normalizeAssetPathIfNeeded(key, row ?? "");
        }
    });

    return out;
}

function normalizeAssetPathIfNeeded(key, value) {
    if (["logo_path", "logo_dark_path", "favicon_path", "login_background_path", "apple_touch_icon_path"].includes(key)) {
        return normalizeAssetPath(value);
    }
    return value ?? "";
}

function setDynamicFavicon(path) {
    if (!path) return;

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }

    link.href = normalizeAssetPath(path);
}

async function safeJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();

    let data;
    try {
        data = JSON.parse(text);
    } catch {
        const preview = text.slice(0, 220).replace(/\s+/g, " ");
        throw new Error(`Respuesta no JSON HTTP ${res.status}: ${preview}`);
    }

    if (!res.ok || data.ok === false || data.success === false) {
        throw new Error(data.error || data.detail || data.message || `HTTP ${res.status}`);
    }

    return data;
}

class SessionActiveError extends Error {
    constructor(message, data = {}) {
        super(message);
        this.name = "SessionActiveError";
        this.code = "SESSION_ALREADY_ACTIVE";
        this.data = data;
    }
}

function normalizePermissions(raw) {
    if (!raw) return [];

    if (Array.isArray(raw)) return raw.filter(Boolean);

    if (typeof raw === "string") {
        try {
            return normalizePermissions(JSON.parse(raw));
        } catch {
            return raw
                .split(",")
                .map((x) => x.trim())
                .filter(Boolean);
        }
    }

    if (typeof raw === "object") {
        return Object.entries(raw)
            .filter(([, value]) => Number(value) === 1 || value === true)
            .map(([key]) => key);
    }

    return [];
}

async function fetchAndStoreMyPermissions(token) {
    if (!token) return [];

    try {
        const res = await fetch(`${API_BASE}/permissions_my_permissions.php`, {
            headers: {
                Authorization: `Bearer ${token}`,
                "X-CANI-AUTH": token,
            },
        });

        const text = await res.text();
        if (!text || !text.trim()) return [];

        const data = JSON.parse(text);
        if (!res.ok || data?.ok === false || data?.success === false) return [];

        const permissions = normalizePermissions(data.permissions || []);

        localStorage.setItem("cani_permissions", JSON.stringify(permissions));
        localStorage.setItem("permissions", JSON.stringify(permissions));

        if (data.role) localStorage.setItem("role", data.role);
        if (data.user?.role) localStorage.setItem("role", data.user.role);
        if (data.user?.id) localStorage.setItem("user_id", String(data.user.id));
        if (data.user?.display_name || data.user?.name || data.user?.full_name) {
            localStorage.setItem("displayName", data.user.display_name || data.user.name || data.user.full_name);
        }
        if (data.user?.avatar_path) localStorage.setItem("profile_photo_url", data.user.avatar_path);

        window.dispatchEvent(new Event("cani-permissions-updated"));

        return permissions;
    } catch (err) {
        console.warn("permissions_my_permissions post-login error:", err);
        return [];
    }
}

function getDefaultRouteByPermissions(permissions = [], role = "pm") {
    const r = String(role || "").toLowerCase();
    const list = normalizePermissions(permissions);
    const has = (key) => list.includes(key);

    if (r === "admin" || r === "administrador") return "/dashboardexecutive";

    // Primero módulos comerciales/institucionales para usuarios sin PMO.
    if (has("opportunities.view")) return "/opportunities";
    if (has("crm_dashboard.view")) return "/crm-dashboard";
    if (has("crm_quality.view")) return "/crm-quality";
    if (has("agenda.view")) return "/agenda";
    if (has("accounts.view")) return "/accounts";
    if (has("contacts.view")) return "/contacts";

    if (has("affiliate_companies.view")) return "/affiliate-companies";
    if (has("affiliations.view")) return "/affiliations";
    if (has("institution.view")) return "/institution";
    if (has("directory.view")) return "/directory";

    // PMO / proyectos solo si realmente tiene permisos.
    if (has("projects.view") || has("tasks.view")) return "/my-work";
    if (has("dashboardexecutive.view")) return "/dashboardexecutive";
    if (has("dashboard.view")) return "/dashboard";

    if (has("users.manage") || has("users.view")) return "/users";
    if (has("permissions.manage")) return "/permissions";
    if (has("platform_settings.view")) return "/platform-settings";

    return "/no-access";
}

async function tryLogin(email, password) {
    const attempts = [];

    // El endpoint oficial es auth_login.php. Si responde 409 con sesión activa,
    // eso NO es endpoint inválido; es una respuesta válida de seguridad.
    for (const endpoint of LOGIN_ENDPOINTS) {
        try {
            const res = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });

            const text = await res.text();
            const preview = text.slice(0, 180).replace(/\s+/g, " ");

            let data;
            try {
                data = JSON.parse(text);
            } catch {
                attempts.push({
                    endpoint,
                    status: res.status,
                    reason: `No JSON: ${preview}`,
                });
                continue;
            }

            if (res.status === 409 || data.code === "SESSION_ALREADY_ACTIVE") {
                throw new SessionActiveError(
                    data.message || data.error || "Este usuario ya tiene una sesión activa.",
                    data
                );
            }

            if (!res.ok || data.ok === false || data.success === false) {
                attempts.push({
                    endpoint,
                    status: res.status,
                    reason: data.error || data.message || data.detail || "Login rechazado",
                });
                continue;
            }

            const token =
                data.token ||
                data.access_token ||
                data.auth_token ||
                data.jwt ||
                data.data?.token ||
                data.data?.access_token ||
                "";

            if (!token) {
                attempts.push({
                    endpoint,
                    status: res.status,
                    reason: "Respuesta JSON válida, pero sin token.",
                });
                continue;
            }

            return {
                endpoint,
                data,
                token,
                user: data.user || data.data?.user || data.data || {},
            };
        } catch (e) {
            if (e?.code === "SESSION_ALREADY_ACTIVE") throw e;

            attempts.push({
                endpoint,
                status: "fetch-error",
                reason: e.message,
            });
        }
    }

    const detail = attempts.map((a) => `${a.endpoint} → ${a.status}: ${a.reason}`).join("\n");
    throw new Error(`No se encontró endpoint de login válido.\n${detail}`);
}

export default function LoginPage() {
    const navigate = useNavigate();

    const [settings, setSettings] = useState({});
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loadingBrand, setLoadingBrand] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [sessionActive, setSessionActive] = useState(null);

    const cfg = useMemo(() => normalizeSettingsPayload(settings), [settings]);

    const platformName = cfg.platform_name || "CLIP · ORBIS PMO";
    const shortName = cfg.platform_short_name || "CLIP ORBIS";
    const commercialName = cfg.commercial_name || "ORBIS PMO";
    const metaDescription = cfg.meta_description || "Plataforma PMO para dirección, control, trazabilidad y toma de decisiones de proyectos.";
    const primaryColor = cfg.primary_color || "#041c2c";
    const secondaryColor = cfg.secondary_color || "#0f172a";
    const accentColor = cfg.accent_color || "#f4b91b";
    const logo = cfg.logo_dark_path || cfg.logo_path || "";
    const bg = cfg.login_background_path || "";

    useEffect(() => {
        async function loadPublicSettings() {
            try {
                setLoadingBrand(true);
                const data = await safeJson(`${API_BASE}/platform_public_settings_get.php`);
                setSettings(data.values || data.settings || {});
                if (data.values?.favicon_path) setDynamicFavicon(data.values.favicon_path);
            } catch (e) {
                console.warn("platform_public_settings_get error:", e);
            } finally {
                setLoadingBrand(false);
            }
        }

        loadPublicSettings();
    }, []);

    useEffect(() => {
        document.title = `${commercialName || "ORBIS PMO"} · Acceso`;
        if (cfg.favicon_path) setDynamicFavicon(cfg.favicon_path);
    }, [commercialName, cfg.favicon_path]);

    async function handleLogin(e) {
        e.preventDefault();
        setError("");
        setSessionActive(null);

        if (!email.trim() || !password.trim()) {
            setError("Captura correo y contraseña.");
            return;
        }

        try {
            setSubmitting(true);

            const result = await tryLogin(email, password);
            const { token, user } = result;

            localStorage.setItem("cani_auth", token);
            localStorage.setItem("token", token);

            if (user.id) localStorage.setItem("user_id", String(user.id));
            if (user.role) localStorage.setItem("role", user.role);

            const name = user.display_name || user.name || user.full_name || email;
            localStorage.setItem("displayName", name);

            if (user.avatar_path) localStorage.setItem("profile_photo_url", user.avatar_path);

            const effectivePermissions = await fetchAndStoreMyPermissions(token);
            const effectiveRole = localStorage.getItem("role") || user.role || "pm";
            const landingPath = getDefaultRouteByPermissions(effectivePermissions, effectiveRole);

            navigate(landingPath, { replace: true });
        } catch (e2) {
            if (e2?.code === "SESSION_ALREADY_ACTIVE") {
                setSessionActive(e2.data || {});
                setError(e2.message || "Este usuario ya tiene una sesión activa.");
            } else {
                setError(e2.message || "No se pudo iniciar sesión.");
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="min-h-screen w-full flex items-center justify-center p-4"
            style={{
                background: bg
                    ? `linear-gradient(135deg, rgba(15,23,42,.84), rgba(15,23,42,.72)), url(${normalizeAssetPath(bg)}) center/cover no-repeat`
                    : `radial-gradient(circle at top left, ${primaryColor}22, transparent 32%), linear-gradient(135deg, ${secondaryColor}, #020617)`,
            }}
        >
            <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[1.1fr_.9fr] gap-6 items-stretch">
                <section className="hidden lg:flex rounded-3xl border border-white/10 bg-white/10 backdrop-blur-xl shadow-2xl p-8 text-white overflow-hidden relative">
                    <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 20% 20%, ${accentColor}, transparent 30%)` }} />
                    <div className="relative z-10 flex flex-col justify-between w-full">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 text-xs font-semibold">
                                <span className="w-2 h-2 rounded-full" style={{ background: accentColor }} />
                                Plataforma PMO Ejecutiva
                            </div>

                            <h1 className="text-4xl font-semibold leading-tight mt-6">
                                {commercialName}
                            </h1>

                            <p className="text-white/70 text-base mt-4 max-w-xl">
                                {metaDescription}
                            </p>
                        </div>

                        <div className="grid grid-cols-3 gap-3 mt-10">
                            {[
                                ["Control", "Trazabilidad ejecutiva"],
                                ["PMO", "Decisiones con evidencia"],
                                ["CRM", "Seguimiento comercial"],
                            ].map(([title, desc]) => (
                                <div key={title} className="rounded-2xl border border-white/10 bg-white/10 p-4">
                                    <div className="text-sm font-semibold">{title}</div>
                                    <div className="text-xs text-white/60 mt-1">{desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <main className="rounded-3xl bg-white shadow-2xl border border-white/20 p-6 md:p-8">
                    <div className="flex items-center gap-3">
                        {logo ? (
                            <img src={normalizeAssetPath(logo)} alt={platformName} className="h-12 max-w-[180px] object-contain" />
                        ) : (
                            <div
                                className="h-12 w-12 rounded-2xl grid place-items-center text-white font-bold"
                                style={{ background: primaryColor }}
                            >
                                {shortName?.slice(0, 2) || "PM"}
                            </div>
                        )}

                        <div>
                            <div className="text-sm font-semibold text-gray-900">{platformName}</div>
                            <div className="text-xs text-gray-500">{commercialName}</div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <h2 className="text-2xl font-semibold text-gray-900">Iniciar sesión</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Accede al centro de control de proyectos, CRM e institución.
                        </p>
                    </div>

                    {error ? (
                        <div className={`mt-5 rounded-2xl border px-4 py-3 text-xs ${sessionActive ? "border-amber-200 bg-amber-50 text-amber-800" : "border-red-200 bg-red-50 text-red-700"}`}>
                            {sessionActive ? (
                                <div className="space-y-2">
                                    <div className="font-semibold">Este usuario ya tiene una sesión activa.</div>
                                    <div>
                                        Cierra sesión en el otro dispositivo o solicita a un administrador liberar la sesión desde Usuarios.
                                    </div>
                                    {sessionActive.expires_at ? (
                                        <div className="text-[11px] opacity-80">
                                            Expira: {sessionActive.expires_at}
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="whitespace-pre-wrap max-h-48 overflow-auto">{error}</div>
                            )}
                        </div>
                    ) : null}

                    {loadingBrand ? (
                        <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50 text-blue-700 px-4 py-3 text-xs">
                            Cargando identidad de plataforma...
                        </div>
                    ) : null}

                    <form onSubmit={handleLogin} className="mt-6 space-y-4">
                        <label className="block">
                            <span className="text-xs font-semibold text-gray-600">Correo</span>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2"
                                style={{ "--tw-ring-color": primaryColor }}
                                placeholder="correo@empresa.com"
                                autoComplete="email"
                            />
                        </label>

                        <label className="block">
                            <span className="text-xs font-semibold text-gray-600">Contraseña</span>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2"
                                style={{ "--tw-ring-color": primaryColor }}
                                placeholder="••••••••"
                                autoComplete="current-password"
                            />
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded-2xl text-white font-semibold py-3 text-sm disabled:opacity-60 shadow-lg"
                            style={{ background: primaryColor }}
                        >
                            {submitting ? "Validando acceso..." : "Entrar"}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-gray-100 text-xs text-gray-400 flex items-center justify-between gap-3">
                        <span>{cfg.public_domain || cfg.domain || "ORBIS PMO"}</span>
                        <span>{cfg.support_email || "Soporte pendiente"}</span>
                    </div>
                </main>
            </div>
        </div>
    );
}
